import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

/**
 * 心理学 - 竖屏「案例故事」风格
 *
 * 场景化叙事：场景描述 → 心理机制揭示 → 生活启示，像讲故事一样讲心理学
 * 参考"壹心理""简单心理"等账号的叙事方式
 * 尺寸：1080x1920 竖屏
 */

interface StoryScene {
  emoji: string;
  narration: string;
}

interface PsychologyStoryProps {
  title: string;
  titleEn: string;
  scenes: StoryScene[];
  mechanismName: string;
  mechanismExplain: string;
  lifeExamples: string[];
  takeaway: string;
  themeGradient?: [string, string];
}

const DEFAULT_PROPS: PsychologyStoryProps = {
  title: '为什么你总觉得别人过得比你好？',
  titleEn: 'Social Comparison Theory',
  scenes: [
    { emoji: '📱', narration: '你打开朋友圈，看到朋友在马尔代夫度假' },
    { emoji: '😔', narration: '你低头看了看自己加班的工位，突然觉得人生好失败' },
    { emoji: '🧠', narration: '但你不知道的是——他刚被裁员，这是"散心旅行"' },
  ],
  mechanismName: '社会比较理论',
  mechanismExplain: '人们通过与他人比较来评估自己的价值。但社交媒体让我们只看到别人的"精选集"，却拿自己的"幕后花絮"去对比',
  lifeExamples: [
    '刷到别人的年薪百万，忘了他 996 的日常',
    '看到别人秀恩爱，忽略了他们也会吵架',
    '羡慕别人的自律早起，却不知他失眠了',
  ],
  takeaway: '停止用别人的高光时刻，丈量自己的普通日常',
  themeGradient: ['#f472b6', '#a78bfa'],
};

export const PsychologyStoryTemplate: React.FC<Partial<PsychologyStoryProps>> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const [color1, color2] = p.themeGradient!;

  // Title (0-60)
  const titleProgress = spring({ frame, fps, config: { damping: 18, stiffness: 80 } });

  // Story scenes (60+)
  const sceneStart = 60;
  const sceneDuration = 80;

  // Current scene
  const sceneFloat = interpolate(
    frame,
    [sceneStart, sceneStart + p.scenes.length * sceneDuration],
    [0, p.scenes.length - 0.01],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
  );
  const currentScene = Math.floor(sceneFloat);

  // Mechanism reveal
  const mechStart = sceneStart + p.scenes.length * sceneDuration + 20;
  const mechOpacity = interpolate(frame, [mechStart, mechStart + 30], [0, 1], { extrapolateRight: 'clamp' });
  const mechY = spring({ frame: frame - mechStart, fps, from: 40, to: 0, config: { damping: 20, stiffness: 80 } });

  // Examples
  const exStart = mechStart + 80;
  const exInterval = 40;

  // Takeaway
  const takeStart = exStart + p.lifeExamples.length * exInterval + 20;
  const takeOpacity = interpolate(frame, [takeStart, takeStart + 25], [0, 1], { extrapolateRight: 'clamp' });
  const takeScale = spring({
    frame: frame - takeStart,
    fps,
    from: 0.9,
    to: 1,
    config: { damping: 12, stiffness: 100 },
  });

  // Ambient orb
  const orbPulse = interpolate(Math.sin(frame / 25), [-1, 1], [0.9, 1.1]);

  return (
    <AbsoluteFill
      style={{
        background: '#0a0a0f',
        fontFamily: '-apple-system, "PingFang SC", "Noto Serif SC", serif',
        color: 'white',
        overflow: 'hidden',
      }}
    >
      {/* Ambient gradient orb */}
      <div
        style={{
          position: 'absolute',
          top: '20%',
          left: '50%',
          width: 500,
          height: 500,
          transform: `translate(-50%, -50%) scale(${orbPulse})`,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${color1}10, transparent 70%)`,
        }}
      />

      {/* Title */}
      <div
        style={{
          position: 'absolute',
          top: 100,
          left: 60,
          right: 60,
          opacity: titleProgress,
          transform: `translateY(${(1 - titleProgress) * 20}px)`,
        }}
      >
        <h1
          style={{
            fontSize: 60,
            fontWeight: 800,
            lineHeight: 1.4,
            margin: 0,
            background: `linear-gradient(135deg, ${color1}, ${color2})`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          {p.title}
        </h1>
        <p style={{ fontSize: 40, color: 'rgba(255,255,255,0.3)', marginTop: 10, fontStyle: 'italic' }}>
          {p.titleEn}
        </p>
      </div>

      {/* Story scenes — sequential reveal */}
      <div
        style={{
          position: 'absolute',
          top: 360,
          left: 60,
          right: 60,
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
        }}
      >
        {p.scenes.map((scene, i) => {
          const start = sceneStart + i * sceneDuration;
          const sceneProgress = spring({
            frame: frame - start,
            fps,
            from: 0,
            to: 1,
            config: { damping: 18, stiffness: 100 },
          });
          const sceneOpacity = interpolate(frame, [start, start + 15], [0, 1], { extrapolateRight: 'clamp' });
          const isActive = i === currentScene;

          return (
            <div
              key={i}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 18,
                padding: '22px 24px',
                background: isActive ? 'rgba(255,255,255,0.04)' : 'transparent',
                borderRadius: 18,
                border: isActive ? `1px solid ${color1}25` : '1px solid transparent',
                opacity: sceneOpacity,
                transform: `translateX(${(1 - sceneProgress) * 30}px)`,
              }}
            >
              <div
                style={{
                  width: 52,
                  height: 52,
                  borderRadius: 16,
                  background: `${color1}12`,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  fontSize: 32,
                  flexShrink: 0,
                }}
              >
                {scene.emoji}
              </div>
              <div
                style={{
                  fontSize: 40,
                  lineHeight: 1.6,
                  color: isActive ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.4)',
                }}
              >
                {scene.narration}
              </div>
            </div>
          );
        })}
      </div>

      {/* Mechanism reveal */}
      <div
        style={{
          position: 'absolute',
          top: 930,
          left: 60,
          right: 60,
          opacity: mechOpacity,
          transform: `translateY(${mechY}px)`,
        }}
      >
        <div
          style={{
            padding: '28px 32px',
            background: `linear-gradient(135deg, ${color1}10, ${color2}08)`,
            borderRadius: 22,
            border: `1px solid ${color1}25`,
          }}
        >
          <div
            style={{
              display: 'inline-block',
              padding: '6px 16px',
              background: `${color1}20`,
              borderRadius: 8,
              fontSize: 35,
              fontWeight: 700,
              color: color1,
              letterSpacing: '0.1em',
              marginBottom: 14,
            }}
          >
            心理机制
          </div>
          <div style={{ fontSize: 40, fontWeight: 700, color: color1, marginBottom: 10 }}>
            {p.mechanismName}
          </div>
          <div style={{ fontSize: 35, lineHeight: 1.7, color: 'rgba(255,255,255,0.75)' }}>
            {p.mechanismExplain}
          </div>
        </div>
      </div>

      {/* Life examples */}
      <div
        style={{
          position: 'absolute',
          top: 1320,
          left: 60,
          right: 60,
        }}
      >
        <div style={{ fontSize: 45, color: 'rgba(255,255,255,0.3)', marginBottom: 14, fontWeight: 600 }}>
          生活中的表现
        </div>
        {p.lifeExamples.map((ex, i) => {
          const start = exStart + i * exInterval;
          const opacity = interpolate(frame, [start, start + 15], [0, 1], { extrapolateRight: 'clamp' });
          const x = interpolate(frame, [start, start + 20], [30, 0], { extrapolateRight: 'clamp' });

          return (
            <div
              key={i}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                marginBottom: 14,
                opacity,
                transform: `translateX(${x}px)`,
              }}
            >
              <div
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: color1,
                  flexShrink: 0,
                  opacity: 0.6,
                }}
              />
              <span style={{ fontSize: 40, color: 'rgba(255,255,255,0.6)', lineHeight: 1.5 }}>{ex}</span>
            </div>
          );
        })}
      </div>

      {/* Takeaway */}
      <div
        style={{
          position: 'absolute',
          bottom: 120,
          left: 60,
          right: 60,
          textAlign: 'center',
          opacity: takeOpacity,
          transform: `scale(${takeScale})`,
        }}
      >
        <div
          style={{
            display: 'inline-block',
            padding: '22px 36px',
            background: `linear-gradient(135deg, ${color1}18, ${color2}18)`,
            borderRadius: 20,
            border: `1px solid ${color1}30`,
          }}
        >
          <div style={{ fontSize: 30, fontWeight: 700, lineHeight: 1.5 }}>
            💡 {p.takeaway}
          </div>
        </div>
      </div>

      {/* Progress */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: `${interpolate(frame, [0, takeStart + 40], [0, 100], { extrapolateRight: 'clamp' })}%`,
          height: 4,
          background: `linear-gradient(90deg, ${color1}, ${color2})`,
        }}
      />
    </AbsoluteFill>
  );
};
